import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useToast } from '../context/ToastContext';
import { useImageGenerationData } from '../hooks/useImageGenerationData';
import { createImageGenerationRequest } from '../services/imageGenerationService';

const DEFAULT_FORM = {
  aspect_ratio: '1024x1024',
  image_count: 1,
  workflow_id: 'flux_upscale',
  prompt_config: {
    type: 'manual',
    value: '',
    group_id: null,
  },
};

const formatRequestStatus = (request) => {
  const remaining = request?.remaining_count ?? 0;
  const failed = request?.failed_count ?? 0;
  const initial = request?.initial_count ?? 0;
  const normalizedStatus = String(request?.status || '').toLowerCase();
  if (normalizedStatus.startsWith('termin') || remaining <= 0) {
    return `Termine - ${Math.max(initial - failed, 0)} ok / ${failed} echec`;
  }
  return `${remaining} restant(s) - ${failed} echec`;
};

const ImageGenerationPage = () => {
  const toast = useToast();
  const { jobs, requests, loading } = useImageGenerationData();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (form.prompt_config.type === 'manual') {
      return Boolean(form.prompt_config.value.trim());
    }
    return true;
  }, [form.prompt_config.type, form.prompt_config.value, submitting]);

  const updatePromptConfig = (patch) => {
    setForm((prev) => ({
      ...prev,
      prompt_config: {
        ...prev.prompt_config,
        ...patch,
      },
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await createImageGenerationRequest({
        aspect_ratio: form.aspect_ratio,
        image_count: Number(form.image_count),
        workflow_id: form.workflow_id,
        prompt_config: {
          type: form.prompt_config.type,
          value: form.prompt_config.type === 'manual' ? form.prompt_config.value : null,
          group_id: form.prompt_config.type === 'group' ? form.prompt_config.group_id : null,
        },
      });
      toast.success(`${response.jobs.length} job(s) image lances`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.message || 'Impossible de lancer la generation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <section className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 shadow-xl overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200 dark:border-white/10 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-white/5 dark:via-white/10 dark:to-white/5">
            <p className="text-xs uppercase tracking-[0.35em] text-orange-500 font-semibold">Image Lab</p>
            <h1 className="mt-2 text-3xl font-bold">Generation d'image</h1>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              V1 RunPod + Firebase. Lancement manuel ou aleatoire, suivi en temps reel, galerie finale.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 grid gap-5 lg:grid-cols-[1.4fr_0.9fr]">
            <div className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-semibold">Source du prompt</label>
                <div className="grid sm:grid-cols-3 gap-3">
                  {[
                    ['manual', 'Prompt manuel'],
                    ['random', 'Aleatoire'],
                    ['group', 'Groupe'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => updatePromptConfig({ type: value })}
                      className={`rounded-2xl border px-4 py-3 text-left transition ${
                        form.prompt_config.type === value
                          ? 'border-orange-400 bg-orange-50 text-orange-700 dark:border-orange-400 dark:bg-orange-500/10 dark:text-orange-200'
                          : 'border-slate-200 bg-slate-50 hover:bg-slate-100 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10'
                      }`}
                    >
                      <span className="font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {form.prompt_config.type === 'manual' && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Prompt</label>
                  <textarea
                    value={form.prompt_config.value}
                    onChange={(event) => updatePromptConfig({ value: event.target.value })}
                    placeholder="Describe the image you want to generate..."
                    rows={6}
                    className="w-full rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-950/60 px-4 py-3 outline-none focus:ring-2 focus:ring-orange-500/40"
                  />
                </div>
              )}

              {form.prompt_config.type === 'group' && (
                <div className="rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 text-sm dark:bg-amber-500/10 dark:text-amber-100 dark:border-amber-500/40">
                  Le mode group est reserve a la phase suivante. Cette V1 expose deja le flux backend, mais n'active pas encore la resolution de groupes.
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Workflow</label>
                  <select
                    value={form.workflow_id}
                    onChange={(event) => setForm((prev) => ({ ...prev, workflow_id: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-950/60 px-3 py-2"
                  >
                    <option value="flux_upscale">flux_upscale</option>
                    <option value="qwen_base">qwen_base</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold">Format</label>
                  <select
                    value={form.aspect_ratio}
                    onChange={(event) => setForm((prev) => ({ ...prev, aspect_ratio: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-950/60 px-3 py-2"
                  >
                    <option value="1024x1024">1024x1024</option>
                    <option value="1024x1536">1024x1536</option>
                    <option value="1536x1024">1536x1024</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold">Nombre d'images</label>
                  <input
                    type="number"
                    min="1"
                    max="8"
                    value={form.image_count}
                    onChange={(event) => setForm((prev) => ({ ...prev, image_count: event.target.value }))}
                    className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-950/60 px-3 py-2"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit || form.prompt_config.type === 'group'}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-rose-500 text-white px-5 py-3 font-semibold shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={submitting ? 'spinner' : 'image'} spin={submitting} />
                  {submitting ? 'Lancement...' : 'Lancer la generation'}
                </button>
              </div>
            </div>
          </form>
        </section>

        <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Lots en cours</h2>
              <span className="text-xs text-slate-500 dark:text-slate-400">{requests.length} lot(s)</span>
            </div>
            {loading && requests.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Chargement...</p>
            ) : requests.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Aucune demande de generation pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {requests.map((request) => (
                  <article
                    key={request.request_id}
                    className="rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-sm">Request {request.request_id?.slice(0, 8)}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {formatRequestStatus(request)}
                        </p>
                      </div>
                      <span className="text-xs rounded-full px-2 py-1 bg-slate-200 dark:bg-white/10">
                        {request.status || 'en cours'}
                      </span>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900/70 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Images generees</h2>
              <span className="text-xs text-slate-500 dark:text-slate-400">{jobs.length} image(s)</span>
            </div>

            {loading && jobs.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Chargement...</p>
            ) : jobs.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Les images finalisees apparaitront ici.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {jobs.map((job) => (
                  <article
                    key={job.job_id}
                    className="overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5"
                  >
                    <div className="aspect-square bg-slate-100 dark:bg-slate-950/60">
                      {job.source_url ? (
                        <img
                          src={job.source_url}
                          alt={job.prompt_text}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-400">
                          <FontAwesomeIcon icon="image" className="text-3xl" />
                        </div>
                      )}
                    </div>
                    <div className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xs rounded-full px-2 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
                          {job.status}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {job.width}x{job.height}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 dark:text-slate-200 line-clamp-3">{job.prompt_text}</p>
                      {job.source_url && (
                        <a
                          href={job.source_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-orange-600 dark:text-orange-300"
                        >
                          <FontAwesomeIcon icon="arrow-up-right-from-square" />
                          Ouvrir l'image
                        </a>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default ImageGenerationPage;
